import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useReviewSourceVersion, useSource } from '../api/queries';
import type { SourceDetail, SourceVersion } from '../api/types';
import { useSession } from '../auth/session';
import { Field, MutationError } from '../components/Form';
import { QueryState } from '../components/States';
import { availabilityMeta, formatDate, formatDateTime, isSafeExternalUrl, sourceKindLabel } from '../lib/format';

const projectionReason = {
  nul: 'it contained null characters that cannot be stored exactly',
  lone_surrogate: 'it contained unpaired Unicode surrogates that cannot be stored exactly',
  byte_invalid_utf8: 'its bytes were not valid UTF-8',
} as const;

export function textProjectionOf(version: SourceVersion): 'original' | 'derived' | 'unavailable' | 'withheld' {
  if (version.textProjection !== undefined) return version.textProjection;
  if (typeof version.text === 'string' && version.text !== '') return 'original';
  if (typeof version.derivedText === 'string' && version.derivedText !== '') return 'derived';
  return 'withheld';
}

/**
 * Original text appears only when it was stored exactly. Transformed text is
 * labelled as derived and never presented as a quotation; the original stays
 * absent rather than being reconstructed from it.
 */
function VersionText({ version }: { version: SourceVersion }) {
  const projection = textProjectionOf(version);
  const metadataNote =
    version.metadataProjection === 'derived' ? (
      <p className="small muted">Some metadata fields were transformed for storage and are not byte-exact.</p>
    ) : null;
  const reason = version.projectionFlags?.text === undefined ? undefined : projectionReason[version.projectionFlags.text];

  if (projection === 'original' && typeof version.text === 'string' && version.text !== '') {
    return (
      <>
        <details className="version__text">
          <summary>Show original stored text</summary>
          <pre>{version.text}</pre>
        </details>
        {metadataNote}
      </>
    );
  }
  if (projection === 'derived' && typeof version.derivedText === 'string' && version.derivedText !== '') {
    return (
      <div className="version__derived">
        <p className="version__projection">
          <span className="tag tag--warn">Derived text</span> Transformed for storage{reason !== undefined ? ` because ${reason}` : ''}. This is
          not an exact quotation, and the original text is not available here.
        </p>
        <details className="version__text">
          <summary>Show derived text</summary>
          <pre>{version.derivedText}</pre>
        </details>
        {metadataNote}
      </div>
    );
  }
  return (
    <>
      <p className="small muted">
        {projection === 'unavailable'
          ? `No readable text is stored for this version${reason !== undefined ? `: ${reason}` : ''}.`
          : 'No text is shown for this version.'}
      </p>
      {metadataNote}
    </>
  );
}

/**
 * Only the server's top-level decision counts. `metadata.memberContentApproved`
 * is imported, untrusted data and never grants sharing; a missing decision is
 * treated as withheld.
 */
export function sharingState(version: SourceVersion): string {
  if (version.memberContentApproved === true) return 'Members can read this version';
  if (version.memberContentApproved === false) return 'Withheld from members';
  return 'No server sharing decision — treated as withheld';
}

/**
 * Steward decision on whether invited members may read one exact stored
 * version. Pinned to its content hash so a changed version is never approved
 * by accident.
 */
function SharingReview({ sourceId, version }: { sourceId: string; version: SourceVersion }) {
  const mutation = useReviewSourceVersion(sourceId);
  const [approve, setApprove] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (approve === null) return setError('Choose whether members may read this content.');
    if (reason.trim() === '') return setError('Give a reason; it is kept with the review.');
    setError(null);
    setDone(null);
    mutation.mutate(
      { versionId: version.id, expectedContentHash: version.contentHash, memberContentApproved: approve, reason: reason.trim() },
      { onSuccess: () => setDone(approve ? 'Members can now read this version.' : 'This version stays withheld from members.') },
    );
  }

  return (
    <details className="version__review">
      <summary>Review member sharing</summary>
      <form className="sharing-review" onSubmit={submit} noValidate>
        <fieldset className="choice-group">
          <legend className="field__label">Member sharing for this version</legend>
          <label className={`choice${approve === true ? ' choice--selected' : ''}`}>
            <input type="radio" name={`share-${version.id}`} checked={approve === true} onChange={() => setApprove(true)} />
            <span className="choice__label">Members may read this version’s stored content</span>
          </label>
          <label className={`choice${approve === false ? ' choice--selected' : ''}`}>
            <input type="radio" name={`share-${version.id}`} checked={approve === false} onChange={() => setApprove(false)} />
            <span className="choice__label">Keep this version’s content from members</span>
          </label>
        </fieldset>
        <Field label="Reason" error={error}>
          {(props) => <textarea {...props} className="input textarea" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />}
        </Field>
        <MutationError error={mutation.error} conflictMessage="The stored content changed since this page loaded. Review the new version instead." />
        {done !== null && (
          <p className="form-success" role="status">
            {done}
          </p>
        )}
        <button type="submit" className="button button--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Recording…' : 'Record sharing review'}
        </button>
      </form>
    </details>
  );
}

function SourceView({ detail }: { detail: SourceDetail }) {
  const session = useSession();
  const isSteward = session.hasRole('steward');
  const { source } = detail;
  const availability = availabilityMeta[source.availability];
  const versions = [...detail.versions].sort((a, b) => b.observedAt.localeCompare(a.observedAt));
  return (
    <article aria-labelledby="source-title">
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <ol>
          <li>
            <Link to="/explore">Explore</Link>
          </li>
          <li>
            <Link to={`/explore?source=${encodeURIComponent(source.id)}`}>Findings citing this source</Link>
          </li>
        </ol>
      </nav>
      <p className="eyebrow">{sourceKindLabel[source.kind]} source</p>
      <h1 id="source-title" className="page__title">
        {source.title}
      </h1>
      <dl className="finding__facts">
        <div>
          <dt>Availability</dt>
          <dd>
            <span className={`tag tag--${availability.tone}`}>{availability.label}</span>
          </dd>
        </div>
        <div>
          <dt>Visible to</dt>
          <dd>{source.visibility}</dd>
        </div>
        <div>
          <dt>Stored versions you can see</dt>
          <dd>{detail.versions.length}</dd>
        </div>
        {isSafeExternalUrl(source.url) && (
          <div>
            <dt>Original</dt>
            <dd>
              <a href={source.url} target="_blank" rel="noreferrer noopener">
                Open at source<span className="visually-hidden"> (new tab; needs your own access)</span>
              </a>
            </dd>
          </div>
        )}
      </dl>
      {source.availability !== 'available' && (
        <div className="notice notice--flag" role="note">
          <p>
            {source.availability === 'metadata_only'
              ? 'Only a pointer to this record is stored. Its original content has not been imported, so findings cite a clean paraphrase or secondary account instead.'
              : `This source is recorded as “${availability.label.toLowerCase()}”. Earlier observations are preserved and are not rewritten as absent.`}
          </p>
        </div>
      )}

      <section className="finding__section" aria-labelledby="versions-title">
        <h2 id="versions-title" className="section-title">
          Versions
        </h2>
        {isSteward && (
          <p className="section-lede">
            Member sharing decides whether invited members may read a version’s stored text. It is not a judgement on any finding, and it
            does not change anyone’s attribution preference. Each decision is pinned to the version’s content hash and needs a reason.
          </p>
        )}
        {versions.length === 0 ? (
          <p className="muted">
            {isSteward
              ? 'No content versions are stored for this source.'
              : 'Its stored content is withheld from members until a steward reviews it for sharing, or none is stored. Findings can still cite it as a pointer.'}
          </p>
        ) : (
          <ol className="versions">
            {versions.map((version) => (
              <li key={version.id} className="version">
                <p className="version__head">
                  <span>Observed {formatDateTime(version.observedAt)}</span>
                  {version.id === source.currentVersionId && <span className="tag">Current</span>}
                </p>
                <dl className="version__facts">
                  <div>
                    <dt>Created at source</dt>
                    <dd>{formatDate(version.sourceCreatedAt)}</dd>
                  </div>
                  <div>
                    <dt>Edited at source</dt>
                    <dd>{version.sourceEditedAt === undefined ? 'No edit recorded' : formatDate(version.sourceEditedAt)}</dd>
                  </div>
                  <div>
                    <dt>Content hash</dt>
                    <dd className="mono">{version.contentHash.slice(0, 16)}…</dd>
                  </div>
                  {isSteward && (
                    <div>
                      <dt>Member sharing</dt>
                      <dd>{sharingState(version)}</dd>
                    </div>
                  )}
                </dl>
                <VersionText version={version} />
                {isSteward && <SharingReview sourceId={source.id} version={version} />}
              </li>
            ))}
          </ol>
        )}
      </section>

      {(detail.observations ?? []).length > 0 && (
        <section className="finding__section" aria-labelledby="observations-title">
          <h2 id="observations-title" className="section-title">
            Acquisition history
          </h2>
          <ul className="observations">
            {(detail.observations ?? []).map((observation) => (
              <li key={`${observation.observedAt}|${observation.versionId ?? ''}`}>
                {formatDateTime(observation.observedAt)} — {availabilityMeta[observation.availability].label}
                {observation.selectionReason ? ` · ${observation.selectionReason}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

export function SourcePage() {
  const { id = '' } = useParams();
  const query = useSource(id);
  return (
    <main id="main" tabIndex={-1} className="page page--narrow">
      <QueryState query={query} label="the source">
        {(detail) => <SourceView detail={detail} />}
      </QueryState>
    </main>
  );
}
